import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, poserJeton, surSessionExpiree } from '../api/client';
import type { Identite } from '../api/types';

interface ContexteAuth {
  moi: Identite | null;
  pret: boolean;
  connecter: (email: string, motDePasse: string) => Promise<void>;
  deconnecter: () => Promise<void>;
}

const Contexte = createContext<ContexteAuth | null>(null);

export function FournisseurAuth({ children }: { children: ReactNode }) {
  const [moi, setMoi] = useState<Identite | null>(null);
  const [pret, setPret] = useState(false);

  // Au chargement, on tente un renouvellement : si le cookie de session est
  // encore valide, l'utilisateur n'a pas à se reconnecter après un F5.
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch('/api/auth/rafraichir', { method: 'POST', credentials: 'include' });
        if (r.ok) {
          const corps = (await r.json()) as { acces: string; utilisateur: Identite };
          if (!annule) {
            poserJeton(corps.acces);
            setMoi(corps.utilisateur);
          }
        }
      } catch {
        // Pas de session : on affiche l'écran de connexion, sans message d'erreur.
      } finally {
        if (!annule) setPret(true);
      }
    })();
    return () => { annule = true; };
  }, []);

  useEffect(() => {
    surSessionExpiree(() => setMoi(null));
  }, []);

  const connecter = useCallback(async (email: string, motDePasse: string) => {
    const r = await api.post<{ acces: string; utilisateur: Identite }>('/api/auth/connexion', { email, motDePasse });
    poserJeton(r.acces);
    setMoi(r.utilisateur);
  }, []);

  const deconnecter = useCallback(async () => {
    try {
      await api.post('/api/auth/deconnexion');
    } finally {
      poserJeton(null);
      setMoi(null);
    }
  }, []);

  const valeur = useMemo(() => ({ moi, pret, connecter, deconnecter }), [moi, pret, connecter, deconnecter]);
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useAuth(): ContexteAuth {
  const c = useContext(Contexte);
  if (!c) throw new Error('useAuth doit être utilisé dans FournisseurAuth.');
  return c;
}
